<script lang="ts">
  /**
   * THE FLOW RAIL PANE — R80 P3 / R115 S2, the MM rail's §FLOW section.
   *
   * Draws minute dir-Δ as a signed bar rail with dir-vega as an overlaid line, from
   * `/api/es/greekflow` (ak `@5f5eda4`). All logic is in `flowRail.ts` so the arms can drive it.
   *
   * ⛔ ONE ROOT, ALWAYS ASKED FOR EXPLICITLY. j's pooled binding says greek-flow is ONE series
   * across SPX/SPXW; a pane that asked for "everything" would let a second root become a second
   * line the day the entitlement gate opens, and two lines that track each other look like
   * agreement rather than a bug. This pane names the root in its ask AND on its face.
   *
   * ⛔ REFUSED IS NOT EMPTY. The route sets `ambiguous` when it will not pick between revisions.
   * That bucket draws NOTHING and is said separately from a missing measure — declining to choose
   * and having no value are different admissions and the reader is owed both.
   */
  import { onMount, onDestroy } from "svelte";
  import { buildFlowRail, flowRailGeometry, flowRailLabel } from "./flowRail";
  import { fetchGreekFlow } from "./api";
  import type { VenueState } from "./api";
  import PanelFreshness from "./PanelFreshness.svelte";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  import { keptPayloadAfterFailure } from "./dataAge";
  import { reportDoorFailure, servedClosure, type DoorFaultEvidence } from "./faultEvidence";

  let { root = "SPX", limit = 240 }: { root?: string; limit?: number } = $props();

  let bars = $state<any[]>([]);
  let payload = $state<any | null>(null);
  let payloadFetchedAtMs = $state<number | null>(null);
  let payloadRequestKey = $state<string | null>(null);
  let served = $state<string | null>(null);
  let ageS = $state<number | null>(null);
  let budgetS = $state(180);
  let servedStale = $state<boolean | null>(null);
  let venueState = $state<VenueState | null>(null);
  let fault = $state<DoorFaultEvidence | null>(null);
  let retainedClockMs = $state<number | null>(null);
  let nowMs = $state(Date.now());
  let loaded = $state(false);
  let infoOpen = $state(false);
  let timer: ReturnType<typeof setInterval> | null = null;
  // GENERATION GUARD: onMount and the $effect both fire at start-up, and without this a SLOW
  // first reply lands AFTER a newer one and silently replaces fresh bars with stale ones.
  let gen = 0;

  async function load() {
    const mine = ++gen;
    const requestKey = `/api/es/greekflow?root=${encodeURIComponent(root)}&limit=${limit}`;
    try {
      // THE HOUSE HELPER, NOT A HAND-ROLLED FETCH. Mine resolved to the PAGES origin, which
      // answers /api/* with 200 + the SPA SHELL — a success that carries no data (api.ts).
      const d = await fetchGreekFlow(root, limit);
      if (mine !== gen) return; // a newer ask already answered — this reply is stale, drop it
      bars = Array.isArray(d.bars) ? d.bars : [];
      payload = d;
      payloadFetchedAtMs = Date.now();
      payloadRequestKey = requestKey;
      // The ROOT IS READ BACK FROM THE ENVELOPE, never assumed from what we asked for — the
      // whole point of the echo is that the reader learns what was SERVED.
      served = typeof d.root === "string" ? d.root : null;
      ageS = typeof d.age_s === "number" ? d.age_s : null;
      budgetS = typeof d.budget_s === "number" && Number.isFinite(d.budget_s) ? d.budget_s : 180;
      servedStale = d.stale;
      venueState = d.venue_state ?? null;
      const newest = bars.reduce((latest, bar) => {
        const stamp = typeof bar?.minute_ts === "string" ? Date.parse(bar.minute_ts) : Number.NaN;
        return Number.isFinite(stamp) ? Math.max(latest, stamp) : latest;
      }, Number.NEGATIVE_INFINITY);
      retainedClockMs = Number.isFinite(newest) ? newest : Date.now();
      fault = null;
    } catch (err) {
      if (mine !== gen) return;
      nowMs = Date.now();
      fault = reportDoorFailure("flow", err, retainedClockMs, nowMs);
      payload = keptPayloadAfterFailure(payload, payloadRequestKey, requestKey);
      if (payload === null) {
        bars = [];
        payloadFetchedAtMs = null;
        payloadRequestKey = null;
      }
    } finally {
      if (mine === gen) loaded = true;
    }
  }

  onMount(() => {
    timer = setInterval(load, 60_000); // the $effect below performs the FIRST load — not both
    const clock = setInterval(() => (nowMs = Date.now()), 1_000);
    return () => clearInterval(clock);
  });
  onDestroy(() => {
    if (timer !== null) clearInterval(timer);
  });
  $effect(() => {
    void root;
    load();
  });

  let rail = $derived(buildFlowRail(bars));
  let label = $derived(flowRailLabel(served));

  const W = 720;
  const H = 108;
  const PAD = 4;



  let geom = $derived(flowRailGeometry(rail, W, H, PAD));
  let drawable = $derived(geom.bars.length > 0 || geom.vegaPath !== "");
  let venueClosed = $derived(servedClosure(venueState));
  let stale = $derived(venueClosed === null && (servedStale === true || (ageS !== null && ageS > budgetS)));
  let freshnessTitle = $derived(
    ageS === null ? "flow receipt age unserved" : `flow receipt age ${Math.round(ageS)}s · budget ${budgetS}s`,
  );
</script>

<section class="flowrail" aria-label="FLOW">
  <header class="panel-id">
    <h3>FLOW</h3>
    <span class="pill">{served ?? "—"}</span>
    <span class="pill">1m</span>
    <span class="hyp">HYP</span>
    <PanelFreshness label="flow" {venueState} {stale} ageTitle={freshnessTitle} />
    <AgePill {payload} fetchedAtMs={payloadFetchedAtMs} door={payloadRequestKey ?? `/api/es/greekflow?root=${encodeURIComponent(root)}&limit=${limit}`} root={served} />
    <PanelInfo id="flow-info" label="Flow information" open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
      <p>{label} · age = receipt {ageS === null ? "UNSERVED" : `${Math.round(ageS)}s`} · budget {budgetS}s</p>
      <p>dir Δ/vega are signed minute measures; the route is pooled-by-vendor and the served root is shown above.</p>
      <p>source table uw_greek_flow_1m_v1</p>
      {#if rail.note}<p>{rail.note}</p>{/if}
    </PanelInfo>
  </header>

  {#if fault}
    <p class="msg" role="alert"><b>DOOR FAILURE</b> · {fault.label}{#if fault.retained} · {fault.retained}{/if}</p>
  {/if}
  {#if !loaded}
    <p class="loading" aria-label="loading flow">●</p>
  {:else if payload !== null && !drawable}
    <p class="msg" role="status"><b>NO DRAWABLE FLOW</b></p>
  {:else if payload !== null && drawable}
    <svg viewBox="0 0 {W} {H}" preserveAspectRatio="none" role="img"
         aria-label="{label}: signed dir-delta per minute as bars around zero. Gaps are minutes with no measure or buckets the route refused.">
      <line class="zero" x1={PAD} x2={W - PAD} y1={geom.zeroY} y2={geom.zeroY} />
      {#each geom.bars as b}
        <rect class="bar" class:pos={b.pos} class:neg={!b.pos} x={b.x} y={b.y} width={b.w} height={b.h} />
      {/each}
      <!-- ⚠ ATTRIBUTION, CORRECTED IN THE LAND (GK-oo, key row @3b571a2): the F1/F2/F3 fix-first
           and the M1 mutation are GK-nn's work. His row was posted under the GK-oo name during a
           12-minute SEAT COLLISION and I copied the signature into SIX comments across three
           files. oo caught ONE line and named it; the class was six, and I corrected all of them
           rather than the instance. ⇒ A MIS-SIGNED ROW PROPAGATES INTO EVERY ARTEFACT THAT CITES
           IT, and the bus row expires while the source comment does not.
           VEGA, DRAWN — not merely named. GK-nn F1: this pane advertised `dir Δ/vega` on the
           owner's face and rendered only Δ, which teaches the reader vega is absent-but-fine. -->
      {#if geom.vegaPath}<path class="vega" d={geom.vegaPath} />{/if}
    </svg>
  {/if}
</section>

<style>
  .flowrail { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .panel-id { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .panel-id h3 { margin: 0; font-size: 12px; letter-spacing: 0.08em; color: var(--ink); }
  .panel-id .pill { display: inline-flex; align-items: center; border: 1px solid var(--line); border-radius: var(--radius-pill); padding: 2px 7px; background: var(--chip); color: var(--muted); font-size: 10px; line-height: 1; }
  .panel-id .hyp { color: var(--faint); }
  svg { width: 100%; height: 108px; display: block; }
  .zero { stroke: var(--muted); stroke-width: 1; opacity: 0.45; }
  /* Signed dealer FLOW wears the CVD token pair — same signed-pressure family, not price up/dn. */
  .bar.pos { fill: var(--cvd-pos); }
  .bar.neg { fill: var(--cvd-neg); }
  .vega { fill: none; stroke: var(--vwap); stroke-width: 1.5; opacity: 0.9; }
  .msg { margin: 0; padding: 10px 0; color: var(--muted); }
  .loading { margin: 0; padding: 10px 0; color: var(--faint); }
</style>
